/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Autocomplete, Grid, TextField, Box, Dropdown, styled } from '@qbe/ads-core';
import { Controller, UseFormReturn } from 'react-hook-form';
import { get } from 'lodash';
import { usePrevious } from 'hooks/usePrevious';
import { AUSTRALIA, AUSTRALIAN_STATE, COMPLAINT } from 'utils/constants';
import { AutocompleteRenderInputParams } from '@mui/material';
import countries from 'resources/addresses';
import { useContent, useContentContext } from '@qbe/dfc-content';
import {
  AddressManualProps,
  AddressManualType,
  FieldCountry,
  FieldName,
  FieldPostcode,
  FieldState,
  FieldStreetAddress,
  FieldSuburb,
} from 'components/types';

const StyledAutocomplete = styled(Autocomplete)`
  .MuiAutocomplete-endAdornment > :nth-child(2) svg path {
    fill: #000000 !important;
  }
`;

export default function AddressManual({ prefix, form }: AddressManualProps) {
  const {
    watch,
    setValue,
    formState: { errors },
    trigger: triggerValidation,
    control,
  } = form as unknown as UseFormReturn<AddressManualType>;

  const { getTextContent } = useContent({ context: useContentContext() });

  // Localized content constants
  const countryLabel = getTextContent('address.country');
  const streetAddressLabel = getTextContent('address.streetAddress');
  const suburbLabel = getTextContent('address.suburb');
  const stateLabel = getTextContent('address.state');
  const postcodeLabel = getTextContent('address.postcode');

  const fieldNameCountry = `${prefix}Country` as FieldCountry;
  const fieldNameState = `${prefix}State` as FieldState;
  const fieldNamePostcode = `${prefix}Postcode` as FieldPostcode;
  const fieldNameStreetAddress = `${prefix}StreetAddress` as FieldStreetAddress;
  const fieldNameSuburb = `${prefix}Suburb` as FieldSuburb;

  const countryValue = watch(fieldNameCountry);
  const isCountryAU = countryValue === AUSTRALIA;
  const isComplaint = watch('lodgement') === COMPLAINT;

  const country = watch(fieldNameCountry);
  const state = watch(fieldNameState);
  const postcode = watch(fieldNamePostcode);

  React.useEffect(() => {
    if ((country || state) && postcode) {
      if (postcode !== ' ') triggerValidation(fieldNamePostcode);
    }
  }, [state, triggerValidation, postcode, country, fieldNamePostcode]);

  const prevCountry = usePrevious(watch(fieldNameCountry));

  const handleChangeCountry = (fieldName: FieldName) => (e: React.SyntheticEvent, value: unknown) => {
    const selectedValue = (value ?? null) as string | null;
    setValue(fieldName, selectedValue);

    triggerValidation(fieldName);

    if (
      (prefix === 'phy' && (prevCountry === AUSTRALIA || selectedValue === AUSTRALIA || country === AUSTRALIA)) ||
      (prefix === 'pos' && (prevCountry === AUSTRALIA || selectedValue === AUSTRALIA || country === AUSTRALIA)) ||
      (prefix === 'repPos' && (prevCountry === AUSTRALIA || selectedValue === AUSTRALIA || country === AUSTRALIA))
    ) {
      setValue(fieldNameState, '');
    } else if (state) {
      triggerValidation(fieldNameState);
    }

    if (postcode) {
      triggerValidation(fieldNamePostcode);
    }
    return selectedValue;
  };

  const handleInputChange = (e: React.SyntheticEvent, value: string): void => {
    if (!value && !isComplaint) setValue(fieldNameCountry, '');

    if (e) triggerValidation(fieldNameCountry);
  };

  // eslint-disable-next-line react/display-name
  const CountryInput = (fieldName: string) => (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      error={!!get(errors, fieldName)}
      helperText={get(errors, fieldName) ? get(errors, fieldName)?.message : null}
      name={fieldName}
      InputLabelProps={{
        ...params.InputLabelProps,
      }}
      label={countryLabel}
      type="Combobox"
      data-track={fieldName}
      data-track-value="capture"
    />
  );

  return (
    <>
      <Box maxWidth={400}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <StyledAutocomplete
              isOptionEqualToValue={(option, value) => option === value}
              value={watch(fieldNameCountry) as FieldCountry}
              options={countries}
              shownIcon
              onChange={handleChangeCountry(fieldNameCountry)}
              onInputChange={handleInputChange}
              renderInput={CountryInput(fieldNameCountry)}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={fieldNameStreetAddress}
              control={control}
              defaultValue={''}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  error={!!errors[fieldNameStreetAddress]}
                  label={streetAddressLabel}
                  id={fieldNameStreetAddress}
                  data-track={fieldNameStreetAddress}
                  data-track-value="mask"
                  inputProps={{
                    maxLength: 225,
                  }}
                  helperText={
                    typeof errors[fieldNameStreetAddress]?.message === 'string'
                      ? errors[fieldNameStreetAddress]?.message
                      : null
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={fieldNameSuburb}
              control={control}
              defaultValue={''}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  error={!!errors[fieldNameSuburb]}
                  label={suburbLabel}
                  id={fieldNameSuburb}
                  data-track={fieldNameSuburb}
                  data-track-value="capture"
                  inputProps={{
                    maxLength: 225,
                  }}
                  helperText={
                    typeof errors[fieldNameSuburb]?.message === 'string' ? errors[fieldNameSuburb]?.message : null
                  }
                />
              )}
            />
          </Grid>
          {isCountryAU && (
            <Grid item xs={12} sm={6} data-test="physical-state-wrapper">
              <Dropdown
                multiple={false}
                error={!!errors[fieldNameState]}
                label={stateLabel}
                placeholder={stateLabel}
                id={fieldNameState}
                name={fieldNameState}
                data-track={fieldNameState}
                data-track-value="capture"
                optionList={AUSTRALIAN_STATE.map((state) => ({ label: state, value: state }))}
                data-test={fieldNameState}
                value={watch(fieldNameState) ?? ''}
                onChange={(event) => {
                  setValue(fieldNameState, (event.target.value as unknown as string) || '');
                  triggerValidation(fieldNameState);
                }}
                helperText={
                  typeof errors[fieldNameState]?.message === 'string' ? errors[fieldNameState]?.message : undefined
                }
                onClose={() => triggerValidation(fieldNameState)}
              />
            </Grid>
          )}
          {!isCountryAU && (
            <Grid item xs={12} sm={6}>
              <Controller
                name={fieldNameState}
                control={control}
                defaultValue={''}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!errors[fieldNameState]}
                    label={stateLabel}
                    id={fieldNameState}
                    data-track={fieldNameState}
                    data-track-value="capture"
                    maxLength={20}
                    helperText={
                      typeof errors[fieldNameState]?.message === 'string' ? errors[fieldNameState]?.message : null
                    }
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
            <Controller
              name={fieldNamePostcode}
              control={control}
              defaultValue={''}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  error={!!errors[fieldNamePostcode]}
                  label={postcodeLabel}
                  id={fieldNamePostcode}
                  data-track={fieldNamePostcode}
                  data-track-value="capture"
                  inputProps={{
                    maxLength: 15,
                  }}
                  helperText={
                    typeof errors[fieldNamePostcode]?.message === 'string' ? errors[fieldNamePostcode]?.message : null
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
